/**
 * Metro 2 Dispute Engine Service
 * 
 * Frontend service for interacting with the Metro 2 dispute engine backend API.
 * Handles round creation, processing, letter generation, and status management.
 */

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api/dispute-engine`;

/**
 * Strategy modes available in the system
 */
export const STRATEGY_MODES = {
  BUREAU_PACKAGE: 'bureau_package',
  SINGLE_ITEM: 'single_item',
  GROUPED: 'grouped',
  DISPUTE_TYPE: 'dispute_type',
  INQUIRY_ONLY: 'inquiry_only',
  PERSONAL_INFO_ONLY: 'personal_info_only',
  ACCOUNTS_SPECIAL: 'accounts_special',
  COLLECTIONS_EXCLUSIVE: 'collections_exclusive',
};

/**
 * Strategy mode descriptions for UI display
 */
export const STRATEGY_MODE_INFO = {
  bureau_package: {
    name: 'Bureau Package',
    description: 'Group findings by bureau (TransUnion, Experian, Equifax). Creates one letter per bureau.',
    icon: 'Building2',
  },
  single_item: {
    name: 'Single Item',
    description: 'One finding per package. Maximum control over each dispute.',
    icon: 'Target',
  },
  grouped: {
    name: 'Grouped by Type',
    description: 'Group findings by rule type (balance, obsolete, identity, etc.).',
    icon: 'Layers',
  },
  dispute_type: {
    name: 'Dispute Category',
    description: 'Group by dispute type (inaccuracy, obsolete, identity).',
    icon: 'FolderOpen',
  },
  inquiry_only: {
    name: 'Inquiries Only',
    description: 'Process only inquiry-related findings.',
    icon: 'Search',
  },
  personal_info_only: {
    name: 'Personal Info Only',
    description: 'Process only personal information disputes.',
    icon: 'User',
  },
  accounts_special: {
    name: 'Special Status Accounts',
    description: 'Target accounts with special Metro 2 status codes (93, 97, G, L).',
    icon: 'AlertTriangle',
  },
  collections_exclusive: {
    name: 'Collections Only',
    description: 'Process only collection accounts.',
    icon: 'Briefcase',
  },
};

/**
 * Template family info for UI display
 */
export const TEMPLATE_FAMILIES = {
  standard: {
    name: 'Standard',
    description: 'Professional, straightforward dispute language. Precise and rights-aware.',
    tone: 'Professional',
    tierAccess: ['auto_starter', 'specialist_pro', 'agency'],
  },
  aggressive: {
    name: 'Aggressive',
    description: 'Stronger pressure with legal authority citation. Fact-first and credible.',
    tone: 'Firm',
    tierAccess: ['specialist_pro', 'agency'],
  },
  compliance: {
    name: 'Compliance',
    description: 'FCRA/FDCPA-focused with regulatory emphasis. Formal and procedural.',
    tone: 'Regulatory',
    tierAccess: ['specialist_pro', 'agency'],
  },
  escalation: {
    name: 'Escalation',
    description: 'For subsequent rounds, references prior disputes. Strongest but grounded.',
    tone: 'Demanding',
    tierAccess: ['specialist_pro', 'agency'],
  },
};

/**
 * Round status definitions (Enhanced Lifecycle)
 * 
 * CRITICAL CLOSED LOGIC:
 * - Letters Ready ≠ Closed
 * - Sent ≠ Closed
 * - Delivered ≠ Closed
 * - Waiting for Response ≠ Closed
 * - Closed ONLY means: final outcome, manual closure, cancelled, resolved, superseded, or escalated
 */
export const ROUND_STATUS = {
  // Creation & Building
  DRAFT: 'draft',
  BUILDING: 'building',
  PROCESSING: 'processing', // Legacy alias for building
  GENERATED: 'generated', // Legacy alias for letters_ready
  
  // Ready for delivery
  LETTERS_READY: 'letters_ready',
  
  // Delivery lifecycle
  DELIVERY_PENDING: 'delivery_pending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  DELIVERY_FAILED: 'delivery_failed',
  
  // Response lifecycle
  WAITING_FOR_RESPONSE: 'waiting_for_response',
  RESPONSE_RECEIVED: 'response_received',
  OUTCOME_REVIEW: 'outcome_review',
  RESPONDED: 'responded', // Legacy alias
  
  // Terminal/Final states - ONLY THESE can be in "Closed" tab
  ESCALATED: 'escalated',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
  SUPERSEDED: 'superseded',
};

export const ROUND_STATUS_INFO = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  building: { label: 'Building', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  generated: { label: 'Findings Generated', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  letters_generated: { label: 'Letters Ready', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  letters_ready: { label: 'Letters Ready', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  delivery_pending: { label: 'Delivery Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  delivered: { label: 'Delivered', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  delivery_failed: { label: 'Delivery Failed', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  waiting_for_response: { label: 'Waiting for Response', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  response_received: { label: 'Response Received', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  outcome_review: { label: 'Review Outcome', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  responded: { label: 'Responded', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  escalated: { label: 'Escalated', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  superseded: { label: 'Superseded', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
};

/**
 * Tab filtering helpers
 */
export const STATUS_FILTERS = {
  // Active: draft, building, delivery_pending, waiting_for_response, response_received, outcome_review
  active: ['draft', 'building', 'processing', 'generated', 'delivery_pending', 'waiting_for_response', 'response_received', 'outcome_review'],
  
  // Ready: letters_ready only
  ready: ['letters_ready', 'letters_generated'],
  
  // Sent: sent, delivered, waiting_for_response (still in progress, not closed)
  sent: ['sent', 'delivered', 'waiting_for_response', 'sent_to_bureau', 'mailed'],
  
  // Closed: ONLY terminal statuses (escalated, resolved, closed, cancelled, superseded)
  // CRITICAL: Does NOT include letters_ready, sent, delivered, waiting_for_response!
  closed: ['escalated', 'resolved', 'closed', 'cancelled', 'superseded'],
};

/**
 * Dispute lifecycle statuses
 */
export const DISPUTE_STATUS = {
  DRAFT: 'draft',
  READY_TO_SEND: 'ready_to_send',
  SENT_TO_BUREAU: 'sent_to_bureau',
  SENT_TO_FURNISHER: 'sent_to_furnisher',
  DELIVERED: 'delivered',
  RESPONSE_RECEIVED: 'response_received',
  REINVESTIGATION_IN_PROGRESS: 'reinvestigation_in_progress',
  CORRECTED: 'corrected',
  DELETED: 'deleted',
  VERIFIED_REMAINS: 'verified_remains',
  ESCALATED: 'escalated',
  CLOSED: 'closed',
};

export const DISPUTE_STATUS_INFO = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: 'FileEdit' },
  ready_to_send: { label: 'Ready to Send', color: 'bg-emerald-100 text-emerald-700', icon: 'Send' },
  sent_to_bureau: { label: 'Sent to Bureau', color: 'bg-amber-100 text-amber-700', icon: 'Building2' },
  sent_to_furnisher: { label: 'Sent to Furnisher', color: 'bg-amber-100 text-amber-700', icon: 'Briefcase' },
  delivered: { label: 'Delivered', color: 'bg-teal-100 text-teal-700', icon: 'CheckCircle' },
  response_received: { label: 'Response Received', color: 'bg-amber-100 text-amber-700', icon: 'Mail' },
  reinvestigation_in_progress: { label: 'Reinvestigation', color: 'bg-orange-100 text-orange-700', icon: 'Search' },
  corrected: { label: 'Corrected', color: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircle2', terminal: true, outcome: 'successful' },
  deleted: { label: 'Deleted', color: 'bg-green-100 text-green-700', icon: 'Trash2', terminal: true, outcome: 'successful' },
  verified_remains: { label: 'Verified / Remains', color: 'bg-red-100 text-red-700', icon: 'XCircle', terminal: true, outcome: 'unsuccessful' },
  escalated: { label: 'Escalated', color: 'bg-rose-100 text-rose-700', icon: 'ArrowUp' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600', icon: 'Archive', terminal: true },
};

export const MAIL_TYPES = [
  { value: 'certified', label: 'Certified Mail' },
  { value: 'certified_return_receipt', label: 'Certified Mail with Return Receipt' },
  { value: 'first_class', label: 'First Class Mail' },
  { value: 'priority', label: 'Priority Mail' },
  { value: 'electronic', label: 'Electronic/Online Submission' },
  { value: 'fax', label: 'Fax' },
];

/**
 * Metro 2 Dispute Engine Service Class
 */
class Metro2Service {
  constructor() {
    this.baseUrl = API;
  }

  /**
   * Set authorization headers
   */
  getHeaders(token) {
    // Always get fresh token from localStorage if not provided
    const authToken = token || localStorage.getItem('fundelite_token');
    return {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };
  }

  /**
   * Make a fetch request with credentials and auth headers
   */
  async fetchWithAuth(url, options = {}) {
    const token = options.token || localStorage.getItem('fundelite_token');
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...this.getHeaders(token),
        ...(options.headers || {}),
      },
    });
  }

  /**
   * Check engine health
   */
  async checkHealth() {
    const response = await this.fetchWithAuth(`${this.baseUrl}/health`);
    return response.json();
  }

  /**
   * Get tier information and permissions
   */
  async getTierInfo(tier, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/tier-info/${tier}`, { token });
    return response.json();
  }

  /**
   * Get available strategy modes for a tier
   */
  async getStrategyModes(token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/strategy-modes`, { token });
    return response.json();
  }

  /**
   * Get available templates for a tier
   */
  async getTemplates(tier, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/templates?tier=${tier}`, { token });
    return response.json();
  }

  /**
   * Analyze accounts/inquiries for disputes (preview without creating round)
   */
  async analyzeForDispute(params, token) {
    const { accountIds, inquiryIds, userId, tier, strategyMode, reportData, consumerData, provider } = params;
    
    const queryParams = new URLSearchParams({
      user_id: userId || '',
      tier: tier || 'auto_starter',
      strategy_mode: strategyMode || 'bureau_package',
      provider: provider || 'identityiq',
    });
    
    accountIds.forEach(id => queryParams.append('account_ids', id));
    inquiryIds.forEach(id => queryParams.append('inquiry_ids', id));
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/rounds/analyze?${queryParams}`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        report_data: reportData,
        consumer_data: consumerData,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Analysis failed');
    }
    
    return response.json();
  }

  /**
   * Analyze a full credit report for dispute recommendations
   * Returns findings and recommended dispute path
   */
  async analyzeReport(userId, tier, provider, reportData, token) {
    // Extract account and inquiry IDs from report data
    const accountIds = (reportData?.tradelines || [])
      .filter(t => t.is_negative)
      .map(t => t.tradeline_id || t.id);
    
    const inquiryIds = (reportData?.inquiries || [])
      .map(i => i.inquiry_id || i.id);
    
    try {
      const result = await this.analyzeForDispute({
        accountIds,
        inquiryIds,
        userId,
        tier,
        strategyMode: 'bureau_package',
        reportData,
        provider,
      }, token);
      
      // Transform findings to include recommendation data
      const findings = (result.findings || []).map(f => ({
        ...f,
        id: f.finding_id || f.id || Math.random().toString(36).substr(2, 9),
        recommended: f.priority === 'high' || f.confidence_score >= 0.7,
        ai_reason: f.reason || f.description,
        issue_type: f.rule_type || f.type || 'general',
        creditor_name: f.account_name || f.creditor_name || f.name,
        type: f.finding_type || f.type || 'account',
        bureau: f.bureau || 'TransUnion',
        fields_affected: f.fields_affected || [],
        priority: f.priority || 'medium',
        confidence_score: f.confidence_score || 0.75,
        has_strong_contradiction: f.confidence_score >= 0.85,
      }));
      
      return {
        success: true,
        findings,
        summary: {
          accounts_analyzed: accountIds.length,
          inquiries_analyzed: inquiryIds.length,
          total_findings: findings.length,
          high_priority: findings.filter(f => f.priority === 'high').length,
        },
        round_id: result.round_id,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        findings: [],
        summary: {},
      };
    }
  }

  /**
   * Create a new dispute round
   */
  async createRound(params, token) {
    const {
      accountIds,
      inquiryIds,
      personalInfoIds,  // NEW: Support PI items
      userId,
      clientId,
      tier,
      strategyMode,
      provider,
      reportData,
      consumerData,
      aiDecision,  // NEW: AI Decision Engine result for strategy-specific generation
    } = params;
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/rounds`, {
      method: 'POST',
      token,
      body: JSON.stringify({
        account_ids: accountIds,
        inquiry_ids: inquiryIds,
        personal_info_ids: personalInfoIds || [],  // NEW: Support PI items
        user_id: userId,
        client_id: clientId,
        tier: tier || 'auto_starter',
        strategy_mode: strategyMode || 'bureau_package',
        provider: provider || 'identityiq',
        report_data: reportData,
        consumer_data: consumerData,
        ai_decision: aiDecision,  // NEW: Pass to backend
      }),
    });
    
    // Clone response before reading to prevent "body stream already read" error
    const clonedResponse = response.clone();
    
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to create round');
    }
    
    return response.json();
  }

  /**
   * Process a dispute round (generate findings and packages)
   */
  async processRound(roundId, userId, token) {
    const queryParams = userId ? `?user_id=${userId}` : '';
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/rounds/${roundId}/process${queryParams}`, {
      method: 'POST',
      token,
    });
    
    // Clone response before reading to prevent "body stream already read" error
    const clonedResponse = response.clone();
    
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to process round');
    }
    
    return response.json();
  }

  /**
   * Get a dispute round by ID
   */
  async getRound(roundId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/rounds/${roundId}`, { token });
    
    // Clone response before reading to prevent "body stream already read" error
    const clonedResponse = response.clone();
    
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to get round');
    }
    
    return response.json();
  }

  /**
   * Specialist Pro workspace read-model: rounds + packages + findings in one request.
   */
  async getSpecialistProWorkspace(userId, token, { status = null, limit = 20, signal } = {}) {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    if (limit) queryParams.append('limit', String(limit));
    const url = `${this.baseUrl}/rounds/user/${userId}/specialist-pro-workspace?${queryParams}`;
    const response = await this.fetchWithAuth(url, { token, signal });

    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to load specialist pro workspace');
    }
    return response.json();
  }

  /**
   * Dispute Center read-model: rounds + packages + letters in one request.
   */
  async getDisputeCenterReadModel(userId, token, { limit = 100, signal } = {}) {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', String(limit));
    const url = `${this.baseUrl}/rounds/user/${userId}/dispute-center?${queryParams}`;
    const response = await this.fetchWithAuth(url, { token, signal });

    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to load dispute center data');
    }
    return response.json();
  }

  /**
   * Soft-delete selected dispute center letters for a user.
   */
  async bulkDeleteDisputeLetters(userId, letterIds, token) {
    const url = `${this.baseUrl}/users/${userId}/letters/bulk-delete`;
    const response = await this.fetchWithAuth(url, {
      token,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ letter_ids: letterIds }),
    });

    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to delete letters');
    }
    return response.json();
  }

  /**
   * Get all rounds for a user
   */
  async getUserRounds(userId, status, limit, token) {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    if (limit) queryParams.append('limit', limit);
    
    const url = `${this.baseUrl}/rounds/user/${userId}${queryParams.toString() ? '?' + queryParams : ''}`;
    
    const response = await this.fetchWithAuth(url, { token });
    
    // Clone response before reading to prevent "body stream already read" error
    const clonedResponse = response.clone();
    
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to get user rounds');
    }
    
    return response.json();
  }

  /**
   * Render letters for a round
   */
  async renderLetters(roundId, templateFamily, userId, token) {
    const queryParams = new URLSearchParams({
      template_family: templateFamily || 'standard',
    });
    if (userId) queryParams.append('user_id', userId);
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/rounds/${roundId}/render-letters?${queryParams}`, {
      method: 'POST',
      token,
    });
    
    // Clone response before reading to prevent "body stream already read" error
    const clonedResponse = response.clone();
    
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to render letters');
    }
    
    return response.json();
  }

  /**
   * Get a rendered letter
   * Returns letter content if ready, or status info if pending/not generated
   */
  async getLetter(roundId, packageId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/rounds/${roundId}/letters/${packageId}`, { token });
    
    // Clone response before reading to prevent "body stream already read" error
    const clonedResponse = response.clone();
    
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to get letter');
    }
    
    const data = await response.json();
    
    // Check if letter is actually generated
    if (data.letter_generated === false) {
      // Return the status info, not an error
      return {
        ...data,
        content: null,
        letter_status: data.letter_status || 'pending'
      };
    }
    
    return data;
  }

  /**
   * Latest unfinished dispute wizard session for resume flow.
   */
  async getResumeCandidate(userId, token) {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/rounds/user/${userId}/resume-candidate`,
      { token }
    );
    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to get resume candidate');
    }
    return response.json();
  }

  /**
   * Persist wizard step on a dispute round for resume support.
   */
  async updateWizardProgressFull(roundId, payload, token) {
    const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
    const base = rawBackendUrl.replace(/\/api$/, '');
    const response = await this.fetchWithAuth(
      `${base}/api/dispute-intelligence/rounds/${roundId}/wizard-progress`,
      {
        method: 'PUT',
        token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to update wizard progress');
    }
    return response.json();
  }

  async updateWizardProgress(roundId, payload, token) {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/rounds/${roundId}/wizard-progress`,
      {
        method: 'PATCH',
        token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to update wizard progress');
    }
    return response.json();
  }

  /**
   * Persist resolved PostGrid packet attachments for a generated letter.
   */
  async putResolvedPacketAttachments(letterId, payload, token) {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/letters/${letterId}/resolved-packet-attachments`,
      {
        method: 'PUT',
        token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    const clonedResponse = response.clone();
    if (!response.ok) {
      let error;
      try {
        error = await clonedResponse.json();
      } catch {
        error = { detail: `HTTP ${response.status}` };
      }
      throw new Error(error.detail || 'Failed to persist packet attachments');
    }
    return response.json();
  }

  /**
   * Get Metro 2 code reference
   */
  async getMetroCodes(token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/metro-codes`, { token });
    return response.json();
  }

  /**
   * Get rules for a provider
   */
  async getRules(provider, version, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/rules/${provider}/${version || '2025-03-01'}`, { token });
    return response.json();
  }

  // ============================================
  // STATUS TRACKING METHODS
  // ============================================

  /**
   * Create a status timeline for a round or package
   */
  async createStatusTimeline(roundId, packageId, userId, token) {
    const queryParams = new URLSearchParams({
      round_id: roundId,
    });
    if (packageId) queryParams.append('package_id', packageId);
    if (userId) queryParams.append('user_id', userId);
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/status/timelines?${queryParams}`, {
      method: 'POST',
      token,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create status timeline');
    }
    
    return response.json();
  }

  /**
   * Get a status timeline by ID
   */
  async getStatusTimeline(timelineId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/status/timelines/${timelineId}`, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get status timeline');
    }
    
    return response.json();
  }

  /**
   * Get status timeline for a round
   */
  async getRoundStatusTimeline(roundId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/status/rounds/${roundId}/timeline`, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get round status');
    }
    
    return response.json();
  }

  /**
   * Get all status timelines for a user
   */
  async getUserStatusTimelines(userId, status, limit, token) {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    if (limit) queryParams.append('limit', limit);
    
    const url = `${this.baseUrl}/status/user/${userId}${queryParams.toString() ? '?' + queryParams : ''}`;
    
    const response = await this.fetchWithAuth(url, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get user status timelines');
    }
    
    return response.json();
  }

  /**
   * Get pending deadlines for a user
   */
  async getPendingDeadlines(userId, daysAhead, token) {
    const queryParams = new URLSearchParams();
    if (daysAhead) queryParams.append('days_ahead', daysAhead);
    
    const url = `${this.baseUrl}/status/user/${userId}/deadlines${queryParams.toString() ? '?' + queryParams : ''}`;
    
    const response = await this.fetchWithAuth(url, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get pending deadlines');
    }
    
    return response.json();
  }

  /**
   * Add a status update to a timeline
   */
  async addStatusUpdate(timelineId, updateData, token) {
    const queryParams = new URLSearchParams({
      status: updateData.status,
    });
    
    if (updateData.destination) queryParams.append('destination', updateData.destination);
    if (updateData.destinationName) queryParams.append('destination_name', updateData.destinationName);
    if (updateData.mailType) queryParams.append('mail_type', updateData.mailType);
    if (updateData.trackingNumber) queryParams.append('tracking_number', updateData.trackingNumber);
    if (updateData.responseDate) queryParams.append('response_date', updateData.responseDate);
    if (updateData.responseSummary) queryParams.append('response_summary', updateData.responseSummary);
    if (updateData.notes) queryParams.append('notes', updateData.notes);
    if (updateData.userId) queryParams.append('user_id', updateData.userId);
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/status/timelines/${timelineId}/update?${queryParams}`, {
      method: 'POST',
      token,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add status update');
    }
    
    return response.json();
  }

  /**
   * Get available statuses
   */
  async getAvailableStatuses(token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/status/statuses`, { token });
    return response.json();
  }

  /**
   * Get mail types
   */
  async getMailTypes(token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/status/mail-types`, { token });
    return response.json();
  }

  // ============================================
  // RESPONSE DOCUMENT METHODS
  // ============================================

  /**
   * Upload a response document
   */
  async uploadDocument(formData, token) {
    // For FormData uploads, don't use fetchWithAuth's default headers
    // which include Content-Type: application/json
    const authToken = token || localStorage.getItem('fundelite_token');
    const response = await fetch(`${this.baseUrl}/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload document');
    }
    
    return response.json();
  }

  /**
   * Get documents for a round
   */
  async getDocumentsForRound(roundId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/documents/round/${roundId}`, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get documents');
    }
    
    return response.json();
  }

  /**
   * Get documents for a timeline
   */
  async getDocumentsForTimeline(timelineId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/documents/timeline/${timelineId}`, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get documents');
    }
    
    return response.json();
  }

  /**
   * Get a specific document
   */
  async getDocument(documentId, token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/documents/${documentId}`, { token });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get document');
    }
    
    return response.json();
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId, userId, token) {
    const queryParams = userId ? `?user_id=${userId}` : '';
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/documents/${documentId}${queryParams}`, {
      method: 'DELETE',
      token,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete document');
    }
    
    return response.json();
  }

  /**
   * Link a document to a status event
   */
  async linkDocumentToStatus(documentId, timelineId, statusEventId, token) {
    const queryParams = new URLSearchParams({
      timeline_id: timelineId,
      status_event_id: statusEventId,
    });
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/documents/${documentId}/link?${queryParams}`, {
      method: 'POST',
      token,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to link document');
    }
    
    return response.json();
  }

  /**
   * Update document metadata (Specialist Pro only)
   * 
   * @param {string} documentId - Document ID
   * @param {Object} updates - Fields to update
   * @param {string} updates.original_filename - New filename
   * @param {string} updates.description - New description
   * @param {string} updates.document_type - New type (response, supporting, evidence)
   * @param {string} updates.source - New source (bureau, furnisher, consumer)
   * @param {string} updates.timeline_id - Link to timeline
   * @param {string} updates.status_event_id - Link to status event
   * @param {number} updates.rotation - Image rotation (0, 90, 180, 270)
   * @param {string} userId - User ID for audit
   * @param {string} token - Auth token
   */
  async updateDocumentMetadata(documentId, updates, userId, token) {
    const queryParams = new URLSearchParams();
    
    if (updates.original_filename !== undefined) queryParams.append('original_filename', updates.original_filename);
    if (updates.description !== undefined) queryParams.append('description', updates.description);
    if (updates.document_type !== undefined) queryParams.append('document_type', updates.document_type);
    if (updates.source !== undefined) queryParams.append('source', updates.source);
    if (updates.timeline_id !== undefined) queryParams.append('timeline_id', updates.timeline_id);
    if (updates.status_event_id !== undefined) queryParams.append('status_event_id', updates.status_event_id);
    if (updates.rotation !== undefined) queryParams.append('rotation', updates.rotation);
    if (userId) queryParams.append('user_id', userId);
    
    const response = await this.fetchWithAuth(`${this.baseUrl}/documents/${documentId}?${queryParams}`, {
      method: 'PATCH',
      token,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update document');
    }
    
    return response.json();
  }

  /**
   * Replace document file (keeps metadata)
   * 
   * @param {string} documentId - Document ID
   * @param {File} file - New file
   * @param {string} userId - User ID for audit
   * @param {string} token - Auth token
   */
  async replaceDocumentFile(documentId, file, userId, token) {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) formData.append('user_id', userId);
    
    // For FormData uploads, don't use fetchWithAuth's default headers
    const authToken = token || localStorage.getItem('fundelite_token');
    const response = await fetch(`${this.baseUrl}/documents/${documentId}/replace`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to replace document file');
    }
    
    return response.json();
  }

  /**
   * Get storage status
   */
  async getStorageStatus(token) {
    const response = await this.fetchWithAuth(`${this.baseUrl}/storage/status`, { token });
    return response.json();
  }
}

// Export singleton instance
export const metro2Service = new Metro2Service();

// Export class for testing
export { Metro2Service };
